/**
 * `src/lib/storage/persist.ts` — ask the browser not to evict our data.
 *
 * D8: there is no backup, export or import. Everything a user writes lives in
 * this browser's IndexedDB and nowhere else. The one thing that costs the user
 * nothing and makes that storage last longer is asking for persistence, so the
 * app asks silently on every boot and shows nothing either way.
 *
 * Called on every boot, not once: Safari is reported to reset the grant when
 * the browser closes, and re-asking is free.
 */

export interface PersistDeps {
	storage?: {
		persist?: () => Promise<boolean>;
		persisted?: () => Promise<boolean>;
	};
}

/** Never throws — some contexts (a sandboxed iframe, site data blocked)
 *  reject rather than return false, and that must not take the boot down. */
export async function requestPersistence(deps: PersistDeps = {}): Promise<boolean> {
	const storage =
		deps.storage ??
		(typeof navigator !== 'undefined' && navigator.storage ? navigator.storage : undefined);
	if (!storage) return false;
	try {
		// `persisted()` first: on some browsers asking again is what re-prompts.
		if (typeof storage.persisted === 'function' && (await storage.persisted())) return true;
		if (typeof storage.persist === 'function') return await storage.persist();
	} catch {
		// Nothing to tell the user; the app works the same without the grant.
	}
	return false;
}
